/**
 * Nightcap — Product Module Web Component
 * Handles: thumbnail gallery, lightbox, quantity, AJAX add-to-cart.
 * After adding to cart, opens the Recharge cart drawer.
 */
class NcProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.quantityInput = this.querySelector('#nc-quantity');
    this.mainImageWrap = this.querySelector('#nc-product-main-image');
    this.mainImage = this.mainImageWrap ? this.mainImageWrap.querySelector('img') : null;
    this.thumbnails = this.querySelectorAll('.nc-product__thumbnail');

    this.lightbox = this.querySelector('[data-lightbox]');
    this.lightboxImg = this.querySelector('[data-lightbox-img]');
    this.lightboxClose = this.querySelector('[data-lightbox-close]');

    this.bindEvents();
  }

  bindEvents() {
    var self = this;

    /* AJAX add-to-cart */
    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.handleSubmit();
      });
    }

    /* Thumbnail clicks */
    this.thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () { self.switchImage(thumb); });
    });

    /* Quantity buttons */
    this.querySelectorAll('.nc-quantity-selector__btn').forEach(function (btn) {
      btn.addEventListener('click', function () { self.updateQuantity(btn.dataset.action); });
    });

    /* Lightbox open */
    if (this.mainImageWrap) {
      this.mainImageWrap.addEventListener('click', function () { self.openLightbox(); });
    }

    /* Lightbox close */
    if (this.lightboxClose) {
      this.lightboxClose.addEventListener('click', function (e) { e.stopPropagation(); self.closeLightbox(); });
    }
    if (this.lightbox) {
      this.lightbox.addEventListener('click', function (e) {
        if (e.target === self.lightbox) self.closeLightbox();
      });
    }

    /* Escape key closes lightbox */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') self.closeLightbox();
    });

    /* Smooth scroll for anchor links */
    this.querySelectorAll('[data-smooth-scroll]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href');
        if (href && href.charAt(0) === '#') {
          var target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    });
  }

  /* ---- AJAX ADD TO CART ---- */

  handleSubmit() {
    var self = this;
    if (!this.form || !this.submitButton) return;

    this.submitButton.setAttribute('disabled', '');
    this.submitButton.classList.add('nc-product__atc-btn--loading');
    var originalText = this.submitButton.textContent;
    this.submitButton.textContent = 'Adding…';

    var formData = new FormData(this.form);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(function (response) {
      if (!response.ok) throw new Error('Add to cart failed: ' + response.status);
      return response.json();
    })
    .then(function () {
      self.submitButton.textContent = 'Added ✓';
      self.openCartDrawer();

      setTimeout(function () {
        self.submitButton.textContent = originalText;
        self.submitButton.removeAttribute('disabled');
        self.submitButton.classList.remove('nc-product__atc-btn--loading');
      }, 1500);
    })
    .catch(function (err) {
      console.error('[Nightcap ATC]', err);
      self.submitButton.textContent = originalText;
      self.submitButton.removeAttribute('disabled');
      self.submitButton.classList.remove('nc-product__atc-btn--loading');
    });
  }

  openCartDrawer() {
    /* 1. Recharge cart drawer toggle */
    var rcToggle = document.querySelector(
      '[data-recharge-cart-toggle], .rc-cart-toggle, [data-rc-cart]'
    );
    if (rcToggle) { rcToggle.click(); return; }

    /* 2. Theme cart icon / header cart link */
    var cartToggle = document.querySelector(
      '.cart-icon-bubble, [data-cart-toggle], header a[href="/cart"]'
    );
    if (cartToggle) { cartToggle.click(); return; }

    /* 3. Fallback — dispatch a custom event for any listener */
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  /* ---- GALLERY ---- */

  switchImage(thumb) {
    var src = thumb.dataset.imageSrc;
    var alt = thumb.dataset.imageAlt;
    if (!this.mainImage || !src) return;

    this.mainImage.src = src;
    this.mainImage.srcset = '';
    if (alt) this.mainImage.alt = alt;
    if (thumb.dataset.imageWidth) this.mainImage.width = thumb.dataset.imageWidth;
    if (thumb.dataset.imageHeight) this.mainImage.height = thumb.dataset.imageHeight;

    this.thumbnails.forEach(function (t) {
      t.classList.remove('nc-product__thumbnail--active');
      t.setAttribute('aria-current', 'false');
    });
    thumb.classList.add('nc-product__thumbnail--active');
    thumb.setAttribute('aria-current', 'true');
  }

  /* ---- LIGHTBOX ---- */

  openLightbox() {
    if (!this.lightbox || !this.mainImage) return;
    this.lightboxImg.src = this.mainImage.src;
    this.lightboxImg.alt = this.mainImage.alt;
    this.lightbox.classList.add('nc-lightbox--open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('nc-lightbox--open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ---- QUANTITY ---- */

  updateQuantity(action) {
    if (!this.quantityInput) return;
    var current = parseInt(this.quantityInput.value, 10) || 1;
    var min = parseInt(this.quantityInput.min, 10) || 1;
    var max = parseInt(this.quantityInput.max, 10) || 99;

    if (action === 'increase' && current < max) {
      this.quantityInput.value = current + 1;
    } else if (action === 'decrease' && current > min) {
      this.quantityInput.value = current - 1;
    }
  }

}

customElements.define('nc-product-form', NcProductForm);

/* Theme editor compatibility */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    var el = section.querySelector('nc-product-form');
    if (el && !el._initialized) {
      el._initialized = true;
    }
  });
}
